import { keyBy } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ProjectId } from 'api/models';
import { Project, ProjectDetailTabKeys } from 'api/project';
import { ProjectTitle } from 'components/molecules/project-details/header/project-title';
import { ProjectDetailTabs } from 'components/molecules/project-details/header/tabs/tabs';
import { useKanbanGroupBy } from 'components/organisms/filter-menu-sidebar/use-group-by-hash';
import { TaskBoard } from 'components/organisms/kanban-board';
import {
  useColumnItems,
  useRowItems,
} from 'components/organisms/kanban-board/group-by';
import { TaskBoardGroupBy } from 'components/organisms/kanban-board/task-group-by';
import { TaskFilterView } from 'components/organisms/tasks/filter-views';
import { useFilters } from 'components/organisms/tasks/use-filters';
import { useFiltersCaching } from 'components/organisms/tasks/use-filters-caching';
import { ProjectTasksViewSwitcher } from 'components/pages/task-list/project-tasks-view-switcher';
import { Neon } from 'components/templates/neon';
import type { RootState } from 'data';
import { useProjectDetail, useProjectPhaseByProjectId } from 'data/projects';
import { useCustomerPreview } from 'hooks/use-customer-preview';
import { useIsExternalUser } from 'hooks/use-user-type';
import { useProjectWebsocket } from 'hooks/websockets';
import { FieldsStore, TaskField } from 'stores/fields';
import { getRandomColor } from 'utils/get-random-color';
import { useProjectTasksRefresh } from '../use-project-tasks-refresh';
import { useProjectTasksSocketUpdates } from '../use-project-tasks-socket-updates';

const excludedTaskFields = [
  'taskId',
  'taskDescription',
  'isCsatEnabled',
  'project',
  'defaultTimeEntryBillable',
  'defaultTimeEntryCategory',
  'privateTaskDescription',
];

export const ProjectTaskBoardPage: FC<{
  project: Project;
}> = ({ project }) => {
  const { projectId } = project;
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const phases = useProjectPhaseByProjectId(projectId);
  const { isPreview } = useCustomerPreview();
  const isExternalUser = useIsExternalUser();
  const isCustomerPortal = isPreview || isExternalUser;

  const excludedFieldsForFilters = useMemo(
    () => [...excludedTaskFields, 'phaseName'],
    [],
  );
  const fields = useMemo<TaskField[]>(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore).filter(
        ({ fieldName }) => !excludedTaskFields.includes(fieldName),
      ),
    [fieldsStore],
  );
  const fieldsForFilter = useMemo(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore)
        .filter(
          ({ fieldName }) => !excludedFieldsForFilters.includes(fieldName),
        )
        .map((field) => {
          return field.fieldName === 'projectPhaseId'
            ? ({
                ...field,
                metaData: {
                  ...field.metaData,
                  options: phases.map((phase) => ({
                    active: true,
                    label: phase?.projectPhaseName,
                    value: phase?.projectPhaseId,
                    color: getRandomColor(phase?.projectPhaseName || ''),
                  })),
                },
              } as TaskField)
            : field;
        }),
    [excludedFieldsForFilters, fieldsStore, phases],
  );
  const fieldsLoading = !FieldsStore.loaded(fieldsStore);
  const fieldsLookupByName = useMemo(
    () =>
      keyBy(
        fieldsForFilter.filter(
          ({ fieldName }) => !excludedFieldsForFilters.includes(fieldName),
        ),
        'fieldName',
      ) as Record<string, TaskField>,
    [excludedFieldsForFilters, fieldsForFilter],
  );

  const { filter, clearFilters, selectFilter } = useFilters(
    fieldsLookupByName,
    fieldsLoading ? 'loading' : 'loaded',
  );

  useFiltersCaching({
    localStorageKey: `task_list.query.project_id-${projectId}`,
  });
  useProjectTasksRefresh({ projectId: ProjectId(projectId) });
  useProjectTasksSocketUpdates(ProjectId(projectId));
  useProjectWebsocket(ProjectId(projectId));

  const [groupBy, setGroupBy] = useKanbanGroupBy(
    ProjectId(projectId),
    fieldsLookupByName,
  );
  const rowFields = useRowItems(fields);
  const columnFields = useColumnItems(fields);
  const groupByItems = useMemo(
    () => [...rowFields, ...columnFields],
    [columnFields, rowFields],
  );

  const { data: projectDetail } = useProjectDetail(ProjectId(projectId));

  return (
    <Neon
      title={<ProjectTitle project={project} />}
      tabs={
        !isCustomerPortal ? (
          <ProjectDetailTabs
            projectId={ProjectId(projectId)}
            activeTabKey={ProjectDetailTabKeys.TaskBoard}
          />
        ) : null
      }
      actions={{
        primary: (
          <TaskFilterView
            isFieldsLoading={fieldsLoading}
            filter={filter}
            fields={fieldsForFilter}
            selectFilter={selectFilter}
            clearFilters={clearFilters}
            lookup={fieldsLookupByName}
            includeSavedViews={!!isCustomerPortal}
          />
        ),
        secondary: (
          <TaskBoardGroupBy
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            columnFields={columnFields}
            rowFields={rowFields}
          />
        ),
        central: isCustomerPortal ? (
          <ProjectTasksViewSwitcher projectId={ProjectId(projectId)} />
        ) : null,
      }}
      content={
        <TaskBoard
          projectDetail={projectDetail}
          groupBy={groupBy}
          filter={filter}
          loading={fieldsLoading}
          groupByItems={groupByItems}
        />
      }
    />
  );
};
